import {GameModel} from "./game.model";
import {ProviderModel} from "./provider.model";
import {environment} from "../../environments/environment";
import {Publisher} from "./publisher.class";

export class Game implements GameModel {
  private _name?: string; //to fix  'RangeError: Maximum call stack size exceeded' errors

  constructor(public raw: GameModel) {
    if (!raw.hash){
      const games = environment.games as GameModel[];
      this.raw = {...raw, ...games.find(game => game?.id == raw.id)}
    }
  }

  get id(): number {
    return this.raw.id
  }

  get name(): string {
    // return this.raw.name
    if (!this._name) {
      this._name = this.raw.name;
    }
    return this._name;
  }

  get image(): string {
    //this is on cdn - we only use this if the cdn fails
    // if (this.name.toLowerCase().includes('freshdeck') || this.publisher.toLowerCase().includes('vivo live')) {
    //   return `assets/games/icons/rival/${this.id}.png`
    // }
    return this.raw.image
  }

  getImage(size: string): string {
    if (this.raw.cdn)
      return this.raw.cdn[size] ?? this.raw.image
    return this.raw.image

  }

  get category(): string {
    return this.raw.category
  }

  get publisher(): string {
    //never use (Sandstrom) in Instanet games publisher name even if Rival is unable to remove it
    //todo do this on cdn
    if (this.raw.publisher.toLowerCase().includes('sandstorm'))
      return this.raw.publisher.replace('(Sandstorm)', '').trim();

    return this.raw.publisher
  }

  get launchUrl(): string | null | undefined {
    return this.raw.launchUrl
  }

  get tag(): null | string {
    return this.raw.tag
  }

  get features(): any[] {
    return this.raw.features
  }

  get enabled(): boolean | undefined {
    return this.raw.enabled
  }

  get slug(): string {
    // return this.raw.slug
    if (this.raw.slug) {
      return this.raw.slug
    }
    return this.raw.name?.toLowerCase().replace(/ /g, '-')
  }

  get sort(): number | undefined {
    return this.raw.sort
  }

  get related(): number[] | undefined {
    return this.raw.related
  }

  get tags(): string[] | undefined {
    return this.raw.tags
  }

  get added(): string | undefined {
    return this.raw.added
  }

  get promoWagerMultiplier(): number | null | undefined {
    return this.raw.promoWagerMultiplier
  }

  get disabledByPromo(): boolean | undefined {
    return this.raw.disabledByPromo
  }

  get isDisabled(): boolean {
    return this.disabledByPromo || this.enabled === false;
  }

  //new filter property for affiliated games from cdn {public? boolean, tracker?: string} | undefined
  get filter() {
    return this.raw.filter
  }


  getDisplayedCategories(): string[] {
    //to tell which categories of our categories has the game
    let result: string[] = [];

    //case "scratch" = 'scratch & win' category from platform OR 'scratch' in tags from cdn
    if(this.category?.toLowerCase().includes("scratch") || this.tags?.includes("scratch")){
      result.push('scratch')
    }
    //case "slots" = 'slots' in category OR in tags
    if(this.category?.toLowerCase().includes("slots") || this.tags?.includes("slots")){
      result.push('slots')
    }
    //case "table" = 'table' in category OR in tags from cdn
    if(this.category?.toLowerCase().includes("table") || this.tags?.includes("table")){
      result.push('table')
    }
    // case "video-poker" = 'video poker' category from platform OR ''video-poker' in tags
    if(this.category?.toLowerCase().includes("video poker") || this.tags?.includes("video-poker")){
      result.push('video-poker')
    }
    //case "specialty" = anything else that not fits in other categories(some instanet games, wingo games, instant-bingo & specialty category from Rival)
    const mainCategories = ["scratch", "slots", "table", "videopoker"];
    if(!this.category || !mainCategories.some(mainCat => this.category?.replace(/\s+/g, "").toLowerCase().includes(mainCat))){
      result.push('specialty');
    }

    //case "live"
    if(this.tags?.includes("live")){
      result.push('live')
    }

    //case "exclusive" = instanet games
    if(this.publisher?.toLowerCase().includes("instanet") || this.tags?.includes('exclusive')){
      result.push('exclusive')
    }
    // case "featured" = has 'featured' tag from platform or from cdn
    if(this.tag?.toLowerCase().includes('featured') || this.tags?.includes("featured")){
      result.push('featured')
    }
    //case "new" = 'new' tag from platform OR from cdn OR TODO check games.added ex. last month ???
    if(this.tag?.toLowerCase().includes('new') || this.tags?.includes("new")){
      result.push('new')
    }
    //case "top" = 'hot' tag from platform OR from cdn
    if(this.tag?.toLowerCase().includes('hot') || this.tags?.includes("hot")){
      result.push('top')
    }

    // console.log(result)
    return result
  }


}

export class Games {

  #games: Game[] = []

  constructor(public games: GameModel[]) {
      this.#games = games.map(game => new Game(game))

  }

  static fromGameArray(games: Game[]){
    return new Games(games.map(game => {
      const rawCopy = {...game.raw}
      rawCopy.image = game.image
      return rawCopy
    }))
  }

  get all(){
    return this.#games
  }

  get length(){
    return this.#games.length
  }

  getById(id: number){
    return this.#games.find(game=> game.id == id)
  }

  getBySlug(slug: string){
    return this.#games.find(game=> game.slug === slug)
  }


  byProviders(providers: ProviderModel[]) {
    //get providers with games dynamically
    const uniquePublishers = Array.from(new Set(this.#games.map(game => game.publisher)));
    const providersWithGames = uniquePublishers.map(publisher => {
      const provider = providers.find(p => publisher.toLowerCase().includes(p.name.toLowerCase()));
      const gameList = this.#games.filter(game => game.publisher.toLowerCase() === publisher.toLowerCase());
      return {
        routerLink: provider ? provider.routerLink : publisher.toLowerCase().split(" ")[0],
        name: publisher,
        imagePath: provider ? provider.imagePath : this.generateProviderSvgPlaceholderUrl(publisher),
        gameList: gameList
      } as ProviderModel;
    });

    // Order providers by the length of the gameList in descending order
    providersWithGames.sort((a, b) => (b.gameList?.length || 0) - (a.gameList?.length || 0));

    return providersWithGames;
  }


  byPublishers(): Publisher[] {
    // get publishers from the game list
    const uniquePublishers = Array.from(new Set(this.#games.map(game => game.publisher)));
    const publishersWithGames = uniquePublishers.map(pName => {
      const gameList = this.#games.filter(game => game.publisher.toLowerCase() === pName.toLowerCase());
      return new Publisher({
        name: pName,
        display_name: pName.split(" (")[0],
        slug: (pName || "").toLowerCase().replace(/ /g,"-").split("-(")[0],
        logo: `https://cdn.0x6e.com/publishers/${(pName||"").toLowerCase().replace(/ /g,"-").split("-(")[0]}/${pName.toLowerCase().replace(/ /g,"-").split("-(")[0]}-logo.svg`,
        weight: 1,
        enabled: true,
        gameList: gameList
      });
    });

    // Order publishers by the length of the gameList in descending order
    publishersWithGames.sort((a, b) => (b.gameList?.length || 0) - (a.gameList?.length || 0));

    return publishersWithGames;
  }



  byCategories(categories: string[]) {
    //get categories with games dynamically TODO - is this needed?
    // const uniquePublishers = Array.from(new Set(this.#games.map(game => game.publisher)));
    // const providersWithGames = uniquePublishers.map(publisher => {
    //   const provider = providers.find(p => publisher.toLowerCase().includes(p.name.toLowerCase()));
    //   const gameList = this.#games.filter(game => game.publisher.toLowerCase() === publisher.toLowerCase());
    //   return {
    //     routerLink: provider ? provider.routerLink : publisher.toLowerCase().split(" ")[0],
    //     name: publisher,
    //     imagePath: provider ? provider.imagePath : this.generateProviderSvgPlaceholderUrl(publisher),
    //     gameList: gameList
    //   } as ProviderModel;
    // });
    //
    // // Order providers by the length of the gameList in descending order
    // providersWithGames.sort((a, b) => (b.gameList?.length || 0) - (a.gameList?.length || 0));
    //
    // return providersWithGames;
  }

  getGamesOfCategory(categoryId: string): Game[] {
    // readonly categories = ['all', 'exclusive', 'featured', 'live', 'new', 'scratch', 'slots', 'table', 'top', 'video-poker',  'specialty'];

    if (!categoryId) {
      console.warn("Invalid category ID:", categoryId);
      return [];
    }

    //get the games of a certain category - based on 'tags[]' from cdn or data provided by platform
    switch (categoryId) {
      case "all":
        return this.#games;
      case "exclusive":
        //instanet games
        return this.#games.filter(game => game.publisher?.toLowerCase().includes("instanet") || game.tags?.includes('exclusive'));
      case "featured":
        //has 'featured' tag from platform or from cdn OR !THIS IS NOT THE SAME - has features[]: game.features?.length
        return this.#games.filter(game => game.tag?.toLowerCase().includes("featured") || game.tags?.includes("featured"));
      case "live":
        //used to check game.name?.includes('live') -> 'live' in cdn tags
        return this.#games.filter(game => game.tags?.includes("live"));
      case "new":
        //new tag from platform OR from cdn OR TODO check games.added ex. last month ???
        return this.#games.filter(game => game.tag?.toLowerCase().includes("new") || game.tags?.includes("new"));
      case "scratch":
        //'scratch & win' category from platform OR 'scratch' in tags from cdn
        return this.#games.filter(game => game.category?.toLowerCase().includes("scratch") || game.tags?.includes("scratch"));
      case "slots":
        //'slots' in category OR in tags (before: + instanet games - they are not all slots anymore)
        return this.#games.filter(game => game.category?.toLowerCase().includes("slots") || game.tags?.includes("slots"));
      case "table":
        //'table' in category OR in tags from cdn
        return this.#games.filter(game => game.category?.toLowerCase().includes("table")|| game.tags?.includes("table"));
      case "top":
        //hot tag from platform OR from cdn OR TODO show recommended games // return this.getMostPlayed(24) ???
        return this.#games.filter(game => game.tag?.toLowerCase().includes("hot") || game.tags?.includes("hot"));
      case "video-poker":
        //'video poker' category from platform OR 'video-poker' in tags
        return this.#games.filter(game => game.category?.toLowerCase().includes("video poker") || game.tags?.includes("video-poker"));
      case "specialty":
        //anything else that not fits in main categories (removed instanet bacause there are special instanet games)
        // = some instanet games, wingo games, instant-bingo & specialty category from Rival
        const mainCategories = ["scratch", "slots", "table", "videopoker"];
        // return this.#games.filter(game => !game.category || !mainCategories.some(mainCat => game.category?.replace(/\s+/g, "").toLowerCase().includes(mainCat)));
        return this.#games.filter(game => {
          const gameCategory= game.category?.replace(/\s+/g, "").toLowerCase() || "";
          return !mainCategories.some(mainCat => gameCategory.includes(mainCat));
        });
      default:
        //if category is not recognized - but can be valid (see our tags - if the tag does not exist, will return [])
        return this.#games.filter(game => game.tags?.includes(categoryId));
    }

  }

  getMostPlayed(count?:number): Game[] {
    //get a list of most popular games
    const sortedAllGames = this.all.slice().sort((a,b)=>{
      // First, prioritize enabled games over disabled ones (on Rival brands) //todo check on GP brands
      if (a.isDisabled && !b.isDisabled) return 1;
      if (!a.isDisabled && b.isDisabled) return -1;


      // Then sort by 'sort' value (lowest first)
      // const sortA = a.sort !== undefined ? a.sort : -Infinity;
      // const sortB = b.sort !== undefined ? b.sort : -Infinity;
      // -Infinity => those games that are not in our db so they dont have sort (ex. new/affiliated), this places them to the beginning of gameList
      const sortA = a.sort !== undefined ? a.sort : Infinity;
      const sortB = b.sort !== undefined ? b.sort : Infinity;
      // return sortB-sortA (descending by sort)
      return sortA-sortB
    })
    if(count){
      return sortedAllGames.slice(0, count)
    }
    return sortedAllGames
  }


  sortBy(option: string){
    //sorting by enabled added for gamingpodium, and does not breaks rival + disabledByPromo added on Rival -> isDisabled in Game class

    // Step 1: sort by 'enabled'/'disabledByPromo' status
    let gamesWithEnabledFirst = this.getMostPlayed().slice(0).sort((a, b) => (a.isDisabled === b.isDisabled) ? 0 : (a.isDisabled ? 1 : -1));

    // Step 2: apply additional sorting based on the selected option
    switch (option) {
      case 'AZ':
        return gamesWithEnabledFirst.sort((a, b) => {
          if (a.isDisabled === b.isDisabled) {
            return a.name.localeCompare(b.name);
          }
          return 0;
        });
      case 'ZA':
        return gamesWithEnabledFirst.sort((a, b) => {
          if (a.isDisabled === b.isDisabled) {
            return b.name.localeCompare(a.name);
          }
          return 0;
        });
      case 'FEATURED':
        return gamesWithEnabledFirst.sort((a, b) => {
          if (a.isDisabled === b.isDisabled) {
            return b.features.length - a.features.length;
          }
          return 0;
        });
      case 'POPULAR':
        return gamesWithEnabledFirst;
      default:
        return gamesWithEnabledFirst;
      }

  }


  generateProviderSvgPlaceholderUrl(text: string): string {
    const svg = `
    <svg width="300" height="150" xmlns="http://www.w3.org/2000/svg">
      <foreignObject width="100%" height="100%">
        <div xmlns="http://www.w3.org/1999/xhtml" style="width:100%;height:100%;display:flex;justify-content:center;align-items:center;text-align:center;color:#fff;font-size:50px;font-weight:bold;line-height:1.2;font-family:sans-serif;">${text}</div>
      </foreignObject>
    </svg>
  `;
    return `data:image/svg+xml;base64,${btoa(svg)}`;
  }




}

